import React, { useContext, useEffect, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { Api, ApiError, api } from './api';
import * as types from './types';
import { useGlobalStore } from 'hooks/useGlobalStore';

// the ApiContext defaults to the api singleton we want to use
// in the deployed app, but we introduce a context to make it easy
// to stub/mock for testing
const ApiContext = React.createContext<Api>(api);

// Wrapper to wait for auth to be available before calling protected endpoints
const useHasSession = () => {
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    if (api.getSession()) {
      setHasSession(true);
    }

    const handleEvent = () => {
      if (api.getSession()) {
        setHasSession(true);
      } else {
        setHasSession(false);
      }
    };

    api.addOnSessionDataSetListener(handleEvent);

    return () => {
      api.removeOnSessionDataSetListener(handleEvent);
    };
  }, []);

  return { hasSession };
};

export function useEmployer(options = {}) {
  const api = useContext(ApiContext);
  return useQuery(['employer'], api.getEmployer, options).data;
}

export function useHousehold(options = {}) {
  const api = useContext(ApiContext);
  return useQuery(['household'], api.getHousehold, options).data;
}

export function usePeople<TData = types.People>(options = {}) {
  const api = useContext(ApiContext);
  return useQuery<types.People, ApiError, TData>(
    ['people'],
    api.getPeople,
    options,
  ).data as TData;
}

export function usePersonMutation(personId: string, options = {}) {
  const queryClient = useQueryClient();
  const api = useContext(ApiContext);
  return useMutation(
    (data: types.PersonUpdate) => api.updatePerson(personId, data),
    {
      ...options,
      onSuccess: (person) => {
        queryClient.setQueryData(['people', person.id], person);
      },
    },
  );
}

export function useCreatePersonMutation(options = {}) {
  const queryClient = useQueryClient();
  const api = useContext(ApiContext);
  return useMutation(api.createPerson, {
    ...options,
    onSuccess: (person) => {
      queryClient.setQueryData(['people', person.id], person);
    },
  });
}

export function useIncomes(options = {}) {
  const api = useContext(ApiContext);
  return useQuery(['incomes'], api.getIncomes, options).data as {
    [k: string]: types.Income;
  };
}

export function useEmergencyFundGoal(options = {}) {
  const api = useContext(ApiContext);
  return useQuery(['goals', 'emergencyfund'], api.getEmergencyFundGoal, options)
    .data as types.EmergencyFundGoal;
}

export function useEmergencyFundGoalMutation(options = {}) {
  const queryClient = useQueryClient();
  const api = useContext(ApiContext);
  return useMutation<
    types.EmergencyFundGoal, // response data type
    types.ApiErrorResponse, // error data type
    Omit<types.EmergencyFundGoalUpdate, 'type'> // request data type
  >(api.updateEmergencyFundGoal, {
    ...options,
    onSuccess: (data) => {
      queryClient.setQueryData(['goals', 'emergencyfund'], data);
    },
  });
}

export function useIntegrationsMutation(options = {}) {
  const queryClient = useQueryClient();
  const api = useContext(ApiContext);
  return useMutation<
    types.OperationStatus, // response data type
    types.ApiErrorResponse, // error data type
    null // request data type
  >(api.updateIntegrations, {
    ...options,
    onSuccess: (data) => {
      queryClient.setQueryData(['integrations'], data);
    },
  });
}
export function useCurrentElections() {
  const api = useContext(ApiContext);
  return useQuery(['benefits', 'currentElections'], api.getCurrentElections);
}

export function usePlans() {
  const api = useContext(ApiContext);
  return useQuery(['plans'], api.getPlans);
}

export function useAccounts() {
  const api = useContext(ApiContext);
  return useQuery(['accounts'], api.getAccounts);
}

export function usePatchAccountsMutation(options = {}) {
  const api = useContext(ApiContext);
  return useMutation(() => api.patchAccounts(), {
    ...options,
  });
}

export function useTypicalServices() {
  const api = useContext(ApiContext);
  return useQuery(['assumptions', 'typicalServices'], api.getTypicalServices);
}

export function useBenefitFocusSubmitBundleIntegration(options = {}) {
  const api = useContext(ApiContext);
  return useMutation(
    ({ bundlesId, bundleKey }: { bundlesId: string; bundleKey: string }) =>
      api.submitBundleToBenefitfocusIntegration(bundlesId, bundleKey),
    {
      ...options,
    },
  );
}

export function useGetClaimsSummary(options = {}) {
  const api = useContext(ApiContext);
  return useQuery(['claims', 'summary'], api.getClaimsSummary, options);
}

export function useRefreshClaimsSummary(options = {}) {
  const api = useContext(ApiContext);
  // This post triggers a longer running full fetch from the upstream source
  // It should only be run on initial opt in
  return useMutation(['claims', 'summary'], api.refreshClaimsSummary, options);
}

export function useUpdateClaimsOptIn(options = {}) {
  const api = useContext(ApiContext);
  return useMutation((optIn: boolean | null) => api.updateClaimsOptIn(optIn), {
    ...options,
  });
}

export function useBundlesSummary(bundlesId: string, bundleKey: string) {
  const api = useContext(ApiContext);
  return useQuery(['bundles', bundlesId, bundleKey], () =>
    api.getBundlesSummary(bundlesId, bundleKey),
  );
}

export function useBenefitTypes() {
  const { hasSession } = useHasSession();
  const {
    globalState: {
      apiState: {
        household: { clientId },
      },
    },
  } = useGlobalStore();

  const api = useContext(ApiContext);
  return useQuery(['benefit-types'], () => api.getBenefitTypes(), {
    enabled: hasSession && !!clientId,
  });
}

export function useLdiData() {
  const api = useContext(ApiContext);
  return useQuery(['ldi', 'data'], () => api.getLdiData());
}

export function useBenefitUses() {
  const api = useContext(ApiContext);
  return useQuery(['benefit-uses'], () => api.getBenefitUses());
}
